// PKCE Utility Functions for Fayda SSO
import { v4 as uuidv4 } from 'uuid';
import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64url';

/**
 * Generate a cryptographically secure code verifier for PKCE
 * @returns {string} A 43-128 character URL-safe string
 */
export const generateCodeVerifier = () => {
  // Generate 32 random bytes (256 bits) and convert to base64url
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  
  // Convert to base64url string
  const base64 = btoa(String.fromCharCode(...array));
  
  // Make it URL-safe and remove padding
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Generate code challenge from code verifier using SHA256
 * @param {string} codeVerifier - The code verifier string
 * @returns {string} The base64url-encoded code challenge
 */
export const generateCodeChallenge = (codeVerifier) => {
  // Hash the code verifier with SHA256
  const hash = sha256(codeVerifier);
  
  // Convert to base64url and remove padding
  return Base64.stringify(hash).replace(/=/g, '');
};

/**
 * Store PKCE state in localStorage
 * @param {string} codeVerifier - The code verifier
 * @param {string} state - The state parameter
 */
export const storePKCEState = (codeVerifier, state) => {
  localStorage.setItem('fayda_code_verifier', codeVerifier);
  localStorage.setItem('fayda_state', state);
  
  // Add timestamp for debugging
  localStorage.setItem('fayda_timestamp', Date.now().toString());
  
  console.log('🔐 PKCE State Stored:', {
    codeVerifier: codeVerifier.substring(0, 10) + '...',
    codeVerifierLength: codeVerifier.length,
    codeVerifierFull: codeVerifier, // Log full value for debugging
    state: state.substring(0, 10) + '...',
    timestamp: new Date().toISOString()
  });
};

/**
 * Retrieve PKCE state from localStorage
 * @returns {object} Object containing codeVerifier and state
 */
export const getPKCEState = () => {
  const codeVerifier = localStorage.getItem('fayda_code_verifier');
  const state = localStorage.getItem('fayda_state');
  const timestamp = localStorage.getItem('fayda_timestamp');
  
  console.log('🔍 PKCE State Retrieved:', {
    hasCodeVerifier: !!codeVerifier,
    hasState: !!state,
    codeVerifierLength: codeVerifier?.length || 0,
    codeVerifierFull: codeVerifier, // Log full value for debugging
    timestamp: timestamp ? new Date(parseInt(timestamp)).toISOString() : 'N/A'
  });
  
  return { codeVerifier, state, timestamp };
};

/**
 * Clear PKCE state from localStorage
 */
export const clearPKCEState = () => {
  localStorage.removeItem('fayda_code_verifier');
  localStorage.removeItem('fayda_state');
  localStorage.removeItem('fayda_timestamp');
  
  console.log('🧹 PKCE State Cleared');
};

/**
 * Validate PKCE parameters
 * @param {string} codeVerifier - The code verifier
 * @param {string} codeChallenge - The code challenge
 * @returns {boolean} True if valid
 */
export const validatePKCE = (codeVerifier, codeChallenge) => {
  if (!codeVerifier || !codeChallenge) {
    console.error('❌ PKCE Validation Failed: Missing parameters');
    return false;
  }
  
  if (codeVerifier.length < 43 || codeVerifier.length > 128) {
    console.error('❌ PKCE Validation Failed: Invalid code verifier length:', codeVerifier.length);
    return false;
  }
  
  // Check if code verifier contains only URL-safe characters
  const urlSafeRegex = /^[A-Za-z0-9_-]+$/;
  if (!urlSafeRegex.test(codeVerifier)) {
    console.error('❌ PKCE Validation Failed: Code verifier contains invalid characters');
    return false;
  }
  
  // Verify code challenge matches
  const expectedChallenge = generateCodeChallenge(codeVerifier);
  if (codeChallenge !== expectedChallenge) {
    console.error('❌ PKCE Validation Failed: Code challenge mismatch');
    console.log('Expected:', expectedChallenge);
    console.log('Received:', codeChallenge);
    return false;
  }
  
  console.log('✅ PKCE Validation Passed');
  return true;
};
